import { LoadingButton } from '@mui/lab';
import { Button, TextField, Typography } from '@mui/material';
import { FC, useCallback, useState } from 'react';

import { BoxRow } from 'src/components/BoxRow';
import { Dialog } from 'src/components/Dialog';
import { Spacer } from 'src/components/Spacer';
import { useAlert } from 'src/services/alert/useAlert';
import { useDeleteFirmMutation } from 'src/services/firm';
import { maybeCall } from 'src/utils/common';

const CONFIRM_WORD = 'DELETE';

interface Props {
  firmId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const DeleteFirmModal: FC<Props> = ({ firmId, isOpen, onClose, onSuccess }) => {
  const { onOpenAlertDelayed } = useAlert();
  const [deleteFirm, { isLoading }] = useDeleteFirmMutation();
  const [input, setInput] = useState('');

  const handleClose = useCallback(() => {
    setInput('');
    onClose();
  }, [onClose]);

  const onSubmit = useCallback(async () => {
    try {
      await deleteFirm({ id: firmId }).unwrap();
      setInput('');
      maybeCall(onSuccess);
      onOpenAlertDelayed({
        message: 'The firm has been permanently deleted.',
        title: 'Firm deleted!',
        type: 'success',
      });
    } catch (err) {
      console.log('Delete firm failure', err);
      onOpenAlertDelayed({
        message: 'There was an error, please try again.',
        title: 'Oops!',
        type: 'error',
      });
    } finally {
      onClose();
    }
  }, [deleteFirm, firmId, onClose, onSuccess, onOpenAlertDelayed]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      sx={{
        alignItems: 'center',
        height: 'auto',
        justifyContent: 'center',
        padding: '30px 60px',
      }}
      withCloseIcon={false}
    >
      <Typography
        component="h2"
        fontSize="24px"
        fontWeight="600"
        sx={{ color: '#D7335C', textAlign: 'center', mb: '12px' }}
      >
        Delete this firm?
      </Typography>
      <Typography
        color="#356482"
        fontSize="16px"
        fontWeight="400"
        sx={{ textAlign: 'center', mb: '20px' }}
      >
        This action cannot be undone. All users belonging to this firm will also be deleted.
        <br />
        Type <strong>{CONFIRM_WORD}</strong> to confirm.
      </Typography>
      <TextField
        autoComplete="off"
        fullWidth
        inputProps={{ style: { textAlign: 'center' } }}
        onChange={(e) => setInput(e.target.value)}
        placeholder={CONFIRM_WORD}
        size="small"
        sx={{ mb: '24px' }}
        value={input}
        variant="outlined"
      />
      <BoxRow sx={{ margin: 0, width: '100%' }}>
        <LoadingButton
          color="error"
          disabled={input !== CONFIRM_WORD || isLoading}
          loading={isLoading}
          onClick={onSubmit}
          sx={{ flex: 1, height: '40px' }}
          variant="contained"
        >
          Delete
        </LoadingButton>
        <Spacer width="20px" />
        <Button
          onClick={handleClose}
          sx={{ flex: 1, height: '40px' }}
          variant="outlined"
        >
          Cancel
        </Button>
      </BoxRow>
    </Dialog>
  );
};
